import { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import { Link } from "react-router-dom";

// react-leaflet's default marker icon is broken under bundlers.
// We register a small custom SVG icon that also colours by score.
function scoreIcon(score, selected, slug) {
  const bg =
    score >= 85 ? "#3A7D6A" :
    score >= 70 ? "#004E59" :
    score >= 55 ? "#C68E5F" :
    score >= 40 ? "#B05C50" :
                  "#4A5F60";
  const ring = selected ? "#E27D60" : "white";
  const size = selected ? 44 : 36;
  const html = `
    <div data-testid="map-marker-${slug || "point"}" style="width:${size}px;height:${size}px;border-radius:50%;background:${bg};color:white;
      display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;
      font-weight:700;font-size:${selected ? 16 : 13}px;
      box-shadow:0 4px 10px rgba(0,0,0,0.25);
      border:3px solid ${ring};transition:transform 200ms ease;">
      ${score ?? "•"}
    </div>`;
  return L.divIcon({
    html,
    className: "surf-map-icon",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2 + 2],
  });
}

function pointIcon(slug) {
  const html = `<div data-testid="map-marker-${slug || "point"}" style="width:14px;height:14px;border-radius:50%;background:#004E59;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`;
  return L.divIcon({ html, className: "surf-map-point", iconSize: [14, 14], iconAnchor: [7, 7] });
}

function FitBounds({ areas, single }) {
  const map = useMap();
  useEffect(() => {
    const pts = areas
      .filter((a) => typeof a.lat === "number" && typeof a.lon === "number")
      .map((a) => [a.lat, a.lon]);
    if (pts.length === 0) return;
    if (pts.length === 1 || single) {
      map.setView(pts[0], 11, { animate: true });
    } else {
      const bounds = L.latLngBounds(pts);
      map.fitBounds(bounds.pad(0.25), { animate: true });
    }
  }, [areas, single, map]);
  return null;
}

function FocusOn({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target?.lat != null && target?.lon != null) {
      map.setView([target.lat, target.lon], Math.max(map.getZoom(), 11), { animate: true });
    }
  }, [target, map]);
  return null;
}

/**
 * Reusable Leaflet map for Surfmatics.
 *
 * Location visibility policy is enforced client-side too:
 * - broad_area: renders an approximated CircleMarker + 2km circle (no pin at exact spot)
 * - sensitive: not rendered on the map at all
 *
 * `areas`: [{ id, name, slug, lat, lon, score, suitability, best_window_start,
 *             best_window_end, visibility, coordinates_approximate, coordinates_hidden }]
 * `selectedId`: current selected surf-area id (highlights the marker)
 * `onSelectArea`: called when a marker is clicked
 * `single`: single-area detail mode
 * `height`: CSS height (defaults 380)
 */
export default function SurfMap({
  areas,
  selectedId,
  onSelectArea,
  single = false,
  height = 380,
  showApproxCircle = true,
}) {
  const visibleAreas = useMemo(
    () =>
      (areas || []).filter(
        (a) =>
          a.visibility !== "sensitive" &&
          !a.coordinates_hidden &&
          typeof a.lat === "number" &&
          typeof a.lon === "number",
      ),
    [areas],
  );

  const center = visibleAreas[0] ? [visibleAreas[0].lat, visibleAreas[0].lon] : [50, 0];

  const selected = visibleAreas.find((a) => a.id === selectedId) || null;
  const mapRef = useRef(null);

  return (
    <div
      className="rounded-2xl overflow-hidden border border-border"
      style={{ height }}
      data-testid="surf-map"
    >
      <MapContainer
        center={center}
        zoom={6}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%" }}
        ref={mapRef}
      >
        <TileLayer
          attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds areas={visibleAreas} single={single} />
        {selected && <FocusOn target={selected} />}

        {visibleAreas.map((a) => {
          const isSelected = a.id === selectedId;
          const approximate =
            a.coordinates_approximate || a.visibility === "broad_area";
          return (
            <div key={a.id}>
              {approximate && showApproxCircle && (
                <Circle
                  center={[a.lat, a.lon]}
                  radius={2000}
                  pathOptions={{
                    color: "#3A7D6A",
                    fillColor: "#3A7D6A",
                    fillOpacity: 0.12,
                    weight: 1,
                    dashArray: "6 6",
                  }}
                />
              )}
              <Marker
                position={[a.lat, a.lon]}
                icon={typeof a.score === "number" ? scoreIcon(a.score, isSelected, a.slug || a.id) : pointIcon(a.slug || a.id)}
                eventHandlers={{
                  click: () => onSelectArea && onSelectArea(a.id),
                }}
              >
                <Popup autoPan={false}>
                  <div className="min-w-[180px]">
                    <div className="font-semibold text-sm text-teal-900">{a.name}</div>
                    {typeof a.score === "number" && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Score {a.score} · {(a.suitability || "").replace(/_/g, " ")}
                      </div>
                    )}
                    {a.best_window_start && (
                      <div className="text-xs mt-1">
                        Best: {new Date(a.best_window_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        {a.best_window_end && (
                          <> – {new Date(a.best_window_end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</>
                        )}
                      </div>
                    )}
                    {approximate && (
                      <div className="text-[10px] mt-1 text-terracotta-700">
                        Location approximate for this area.
                      </div>
                    )}
                    {a.slug && (
                      <Link
                        to={`/surf-area/${a.slug}`}
                        className="text-xs mt-2 inline-block text-teal-800 font-semibold underline underline-offset-4"
                        data-testid={`map-detail-link-${a.slug}`}
                      >
                        View details →
                      </Link>
                    )}
                  </div>
                </Popup>
              </Marker>
            </div>
          );
        })}
      </MapContainer>
    </div>
  );
}
